import React, { useState } from 'react';
import { demoChapterStackColumnStyle, demoSectionCardStackStyle, DEMO_SECTION_CARD_STACK_GAP_PX } from '../designSystem';

const FONT_BODY = "'Plus Jakarta Sans', -apple-system, sans-serif";
const FONT_SERIF = "'Playfair Display', Georgia, serif";

const slabel = {
  fontSize: 10,
  fontWeight: 700,
  color: 'var(--muted)',
  letterSpacing: '0.13em',
  textTransform: 'uppercase',
  marginBottom: 8,
};

function Toggle({ on, onClick, onBackground }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      style={{
        width: 34,
        height: 20,
        borderRadius: 10,
        border: 'none',
        padding: 3,
        cursor: 'pointer',
        background: on ? (onBackground || 'var(--pink)') : 'var(--sand-2)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: on ? 'flex-end' : 'flex-start',
        flexShrink: 0,
      }}
    >
      <span
        style={{
          width: 14,
          height: 14,
          borderRadius: '50%',
          background: 'white',
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }}
      />
    </button>
  );
}

function DataRow({ label, value, valueAccent, accentColor = 'var(--pink-d)' }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '9px 0',
        borderBottom: '0.5px solid var(--sand-2)',
        gap: 12,
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
      <span
        style={{
          fontSize: 13,
          color: valueAccent ? accentColor : 'var(--muted)',
          fontWeight: valueAccent ? 600 : 400,
          textAlign: 'right',
        }}
      >
        {value}
      </span>
    </div>
  );
}

/**
 * /demo/fertility/profile and /demo/trying-to-conceive/profile — chapter from URL.
 */
export default function DemoProfilePage({ chapter }) {
  const isTtc = chapter === 'trying-to-conceive';
  const accentD = isTtc ? 'var(--purple-d)' : 'var(--pink-d)';
  const toggleOnBg = isTtc ? 'var(--purple)' : 'var(--pink)';
  const [toggles, setToggles] = useState({
    nova1: true,
    nova2: true,
    nova3: true,
    nova4: true,
    ev1: true,
    ev2: true,
    co1: true,
    co2: false,
    co3: false,
    pr1: true,
    pr2: false,
    pr3: false,
    pr4: false,
  });

  const flip = (k) => setToggles((prev) => ({ ...prev, [k]: !prev[k] }));

  return (
    <div
      style={{
        ...demoChapterStackColumnStyle,
        fontFamily: FONT_BODY,
        boxSizing: 'border-box',
        width: '100%',
      }}
    >
      {/* Hero */}
      <div
        style={{
          background: isTtc
            ? 'linear-gradient(135deg, #eeedfe 0%, #e1f5ee 100%)'
            : 'linear-gradient(135deg, #fbeaf0 0%, #e1f5ee 100%)',
          border: isTtc ? '0.5px solid var(--purple-m, #AFA9EC)' : '0.5px solid var(--pink-m, #f4c0d1)',
          borderRadius: 'var(--r, 14px)',
          padding: 18,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
        }}
      >
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: '50%',
            background: isTtc ? 'var(--purple-l)' : 'var(--pink-l)',
            color: isTtc ? 'var(--purple-d)' : 'var(--pink-d)',
            fontSize: 20,
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: isTtc ? '0 0 0 2px var(--purple-m)' : '0 0 0 2px var(--pink-m)',
            flexShrink: 0,
          }}
        >
          M
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT_SERIF, fontSize: 20, fontWeight: 700, color: '#4b1528', marginBottom: 2 }}>Maya</div>
          <div style={{ fontSize: 12, color: isTtc ? 'var(--purple-d)' : 'var(--pink-d)', marginBottom: 8 }}>
            IVF · Cycle 2 · Stim day 8 · Columbus, OH · Joined Feb 2025
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Low AMH', 'Antagonist protocol', 'Ohio Fertility Network'].map((t) => (
              <span
                key={t}
                style={{
                  background: isTtc ? 'var(--purple-l)' : 'var(--pink-l)',
                  color: isTtc ? 'var(--purple-d)' : 'var(--pink-d)',
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 8px',
                  borderRadius: 10,
                }}
              >
                {t}
              </span>
            ))}
            <span
              style={{
                background: 'var(--sand-2)',
                color: 'var(--muted)',
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 10,
              }}
            >
              2 years trying
            </span>
          </div>
        </div>
        <button
          type="button"
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: accentD,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            whiteSpace: 'nowrap',
            fontFamily: FONT_BODY,
            flexShrink: 0,
          }}
        >
          Edit profile →
        </button>
      </div>

      {/* Nova bar — directly under gradient hero (matches /demo/fertility/* shell) */}
      <div
        style={{
          background: isTtc
            ? 'linear-gradient(135deg, #eeedfe 0%, #fffcf9 100%)'
            : 'linear-gradient(135deg, #fbeaf0 0%, #fffcf9 100%)',
          border: isTtc ? '0.5px solid var(--purple-m)' : '0.5px solid #f4c0d1',
          borderLeft: isTtc ? '3px solid var(--purple-d)' : '3px solid #993556',
          borderRadius: 'var(--r, 14px)',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          padding: '14px 18px',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            minWidth: 32,
            borderRadius: '50%',
            background: '#fffcf9',
            boxShadow: isTtc
              ? '0 0 0 1.5px var(--purple), 0 0 0 3.5px #fffcf9'
              : '0 0 0 1.5px #d4537e, 0 0 0 3.5px #fffcf9',
            fontFamily: FONT_SERIF,
            fontStyle: 'italic',
            fontWeight: 900,
            fontSize: 13,
            color: isTtc ? 'var(--purple-d)' : '#993556',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          N
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: isTtc ? 'var(--purple-d)' : '#993556' }}>Nova</span>
            <span
              style={{
                fontSize: 10,
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: 20,
                background: isTtc ? 'rgba(127,119,221,0.18)' : 'rgba(212,83,126,0.13)',
                color: isTtc ? 'var(--purple-d)' : '#993556',
              }}
            >
              Profile tip
            </span>
          </div>
          <p style={{ fontSize: 13, color: '#4b1528', lineHeight: 1.55, margin: 0 }}>
            The more you share about your journey, the more Nova can personalise — from the questions she prepares before your scan, to the women she matches you with, to the articles she surfaces each day. Your loss history, cycle outcomes, and treatment timeline are the most important inputs. You can edit or add to any section at any time.
          </p>
        </div>
        <button
          type="button"
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: isTtc ? 'var(--purple-d)' : '#993556',
            whiteSpace: 'nowrap',
            cursor: 'pointer',
            background: 'none',
            border: 'none',
            alignSelf: 'center',
            flexShrink: 0,
            fontFamily: FONT_BODY,
          }}
        >
          Update journey →
        </button>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
        {[
          { n: '2', l: 'IVF cycles' },
          { n: '24', l: 'Months trying' },
          { n: '2', l: 'Pregnancy losses' },
        ].map((s) => (
          <div
            key={s.l}
            style={{
              background: 'var(--card, #fffdf9)',
              border: '0.5px solid var(--border)',
              borderRadius: 'var(--r)',
              padding: '14px 16px',
              textAlign: 'center',
            }}
          >
            <div style={{ fontSize: 28, fontWeight: 700, color: 'var(--text)', lineHeight: 1 }}>{s.n}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 4 }}>{s.l}</div>
          </div>
        ))}
      </div>

      {/* Two columns */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          gap: 14,
          alignItems: 'start',
        }}
      >
        {/* Left — My journey */}
        <div
          style={{
            background: 'var(--card)',
            border: '0.5px solid var(--border)',
            borderRadius: 'var(--r)',
            padding: '16px 18px',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: 14,
              gap: 10,
            }}
          >
            <div>
              <h2 className="section-title" style={{ margin: 0 }}>
                My journey
              </h2>
              <p className="section-descriptor" style={{ margin: '4px 0 0' }}>
                How Nova personalises your experience
              </p>
            </div>
            <button
              type="button"
              style={{ fontSize: 11, fontWeight: 600, color: accentD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT_BODY }}
            >
              Edit
            </button>
          </div>

          <div style={slabel}>Trying to conceive</div>
          <DataRow label="Started trying" value="March 2023" />
          <DataRow label="Time trying" value="24 months" valueAccent accentColor={accentD} />
          <DataRow label="Trying with" value="Partner · male factor ruled out" />
          <DataRow label="Primary diagnosis" value="Low ovarian reserve (DOR)" />
          <DataRow label="Secondary findings" value="None confirmed" />
          <div style={{ borderBottom: 'none' }}>
            <DataRow label="Clinic" value="Ohio Fertility Network · Dr. Kim" />
          </div>

          <div style={{ height: 0.5, background: 'var(--border)', margin: '12px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={slabel}>IVF cycles</div>
            <button
              type="button"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: accentD,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 4,
                fontFamily: FONT_BODY,
              }}
            >
              <span
                style={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  background: 'var(--pink-l)',
                  color: 'var(--pink-d)',
                  fontSize: 12,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                +
              </span>
              Add cycle
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div style={{ background: 'var(--sand)', borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', marginBottom: 6 }}>Cycle 1 · Aug 2024</div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Protocol</span>
                <span>Antagonist</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Eggs retrieved</span>
                <span>4</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Fertilized</span>
                <span>3</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Blasts frozen</span>
                <span>1 (4AA)</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Outcome</span>
                <span style={{ color: '#633806' }}>FET planned</span>
              </div>
            </div>
            <div
              style={{
                background: 'var(--sand)',
                borderRadius: 8,
                padding: '10px 12px',
                border: isTtc ? '1.5px solid var(--purple-m)' : '1.5px solid var(--pink-m)',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: accentD,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                }}
              >
                Cycle 2 · Mar 2025 · Active
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Protocol</span>
                <span>Antagonist</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Stim day</span>
                <span>8 of ~14</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Follicles</span>
                <span>6 responding</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ color: 'var(--muted)' }}>Est. retrieval</span>
                <span>~Mar 25</span>
              </div>
              <div style={{ fontSize: 11, display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: 'var(--muted)' }}>Outcome</span>
                <span style={{ color: accentD }}>In progress</span>
              </div>
            </div>
          </div>

          <div style={{ height: 0.5, background: 'var(--border)', margin: '12px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <div style={slabel}>Pregnancy losses</div>
            <button
              type="button"
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: accentD,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: FONT_BODY,
              }}
            >
              + Add
            </button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ background: 'var(--sand)', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ba7517', marginTop: 4, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Chemical pregnancy · June 2023</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>4 weeks · natural conception · first loss</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--amber-l)', color: 'var(--amber)', padding: '2px 8px', borderRadius: 8 }}>Chemical</span>
                  <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--sand-2)', color: 'var(--muted)', padding: '2px 8px', borderRadius: 8 }}>Natural cycle</span>
                </div>
              </div>
            </div>
            <div style={{ background: 'var(--sand)', borderRadius: 8, padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ba7517', marginTop: 4, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)' }}>Miscarriage · November 2023</div>
                <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>7 weeks · natural conception · no D&C</div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--amber-l)', color: 'var(--amber)', padding: '2px 8px', borderRadius: 8 }}>Early miscarriage</span>
                  <span style={{ fontSize: 10, fontWeight: 600, background: 'var(--sand-2)', color: 'var(--muted)', padding: '2px 8px', borderRadius: 8 }}>Natural cycle</span>
                </div>
              </div>
            </div>
          </div>

          <div style={{ height: 0.5, background: 'var(--border)', margin: '12px 0' }} />

          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <div style={slabel}>Prior treatments</div>
            <button type="button" style={{ fontSize: 11, fontWeight: 600, color: accentD, background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT_BODY }}>
              + Add
            </button>
          </div>
          <DataRow label="IUI attempts" value="2 · both unsuccessful" />
          <DataRow label="Clomid / Letrozole" value="3 cycles Letrozole · 2023" />
          <div style={{ borderBottom: 'none' }}>
            <DataRow label="Other interventions" value="None" />
          </div>

          <div style={{ height: 0.5, background: 'var(--border)', margin: '12px 0' }} />

          <div style={slabel}>Current chapter</div>
          <DataRow label="Chapter" value={isTtc ? 'Trying to conceive' : 'Fertility · IVF'} />
          <DataRow label="Location" value="Columbus, OH" />
          <div style={{ borderBottom: 'none' }}>
            <DataRow label="Age" value="34" />
          </div>
        </div>

        {/* Right column */}
        <div style={{ ...demoSectionCardStackStyle, gap: DEMO_SECTION_CARD_STACK_GAP_PX }}>
          <div style={{ background: 'var(--card)', border: '0.5px solid var(--border)', borderRadius: 'var(--r)', padding: '16px 18px' }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              Notifications
            </h2>
            <p className="section-descriptor" style={{ margin: '4px 0 12px' }}>
              Choose what Nova and the platform send you
            </p>

            <div style={{ ...slabel, marginTop: 4 }}>Nova</div>
            {[
              { k: 'nova1', title: 'Daily check-in', sub: 'Morning briefing each stim day' },
              { k: 'nova2', title: 'Injection reminders', sub: '30 min before each dose' },
              { k: 'nova3', title: 'Scan prep', sub: 'Night before monitoring appointments' },
              { k: 'nova4', title: 'Lab result reads', sub: 'When Nova analyses a new result' },
            ].map((row, i) => (
              <div
                key={row.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: i < 3 ? '0.5px solid var(--sand-2)' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{row.sub}</div>
                </div>
                <Toggle on={toggles[row.k]} onClick={() => flip(row.k)} onBackground={toggleOnBg} />
              </div>
            ))}

            <div style={{ height: 0.5, background: 'var(--border)', margin: '8px 0 12px' }} />

            <div style={slabel}>Events</div>
            {[
              { k: 'ev1', title: 'Event invites', sub: 'When someone invites you' },
              { k: 'ev2', title: 'Event reminders', sub: '24hr and 1hr before events' },
            ].map((row, i) => (
              <div
                key={row.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: i === 0 ? '0.5px solid var(--sand-2)' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{row.sub}</div>
                </div>
                <Toggle on={toggles[row.k]} onClick={() => flip(row.k)} onBackground={toggleOnBg} />
              </div>
            ))}

            <div style={{ height: 0.5, background: 'var(--border)', margin: '8px 0 12px' }} />

            <div style={slabel}>Community</div>
            {[
              { k: 'co1', title: 'Replies to my posts', sub: 'When someone replies' },
              { k: 'co2', title: 'Connect requests', sub: 'When a matched woman wants to connect' },
              { k: 'co3', title: 'New group activity', sub: "In groups you've joined" },
            ].map((row, i) => (
              <div
                key={row.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: i < 2 ? '0.5px solid var(--sand-2)' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{row.sub}</div>
                </div>
                <Toggle on={toggles[row.k]} onClick={() => flip(row.k)} onBackground={toggleOnBg} />
              </div>
            ))}
          </div>

          <div style={{ background: 'var(--card)', border: '0.5px solid var(--border)', borderRadius: 'var(--r)', padding: '16px 18px' }}>
            <h2 className="section-title" style={{ margin: 0 }}>
              Privacy
            </h2>
            <p className="section-descriptor" style={{ margin: '4px 0 12px' }}>Control who sees your information</p>
            {[
              { k: 'pr1', title: 'Show me in Women like you', sub: 'Women on similar timelines can see your profile' },
              { k: 'pr2', title: 'Share chapter publicly', sub: 'Fertility chapter shown on your community profile' },
              { k: 'pr3', title: 'Show loss history to matches', sub: 'Women Nova matches to you can see your loss history' },
              { k: 'pr4', title: 'Allow clinic data sharing', sub: 'Share anonymised data with your clinic for research' },
            ].map((row, i) => (
              <div
                key={row.k}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 0',
                  borderBottom: i < 3 ? '0.5px solid var(--sand-2)' : 'none',
                  alignItems: 'flex-start',
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{row.title}</div>
                  <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>{row.sub}</div>
                </div>
                <Toggle on={toggles[row.k]} onClick={() => flip(row.k)} onBackground={toggleOnBg} />
              </div>
            ))}
          </div>

          <div
            style={{
              padding: '12px 16px',
              background: 'var(--card)',
              border: '0.5px solid var(--border)',
              borderRadius: 'var(--r)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <span style={{ fontSize: 12, color: 'var(--muted)' }}>Joined February 2025</span>
            <button type="button" style={{ fontSize: 12, fontWeight: 600, color: '#e24b4a', background: 'none', border: 'none', cursor: 'pointer', fontFamily: FONT_BODY }}>
              Sign out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
